import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Spinner, Button, Form, Tabs, Tab } from 'react-bootstrap';
import Filter from './components/filter/filter';
import { useHistory } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';
import './checker-reports-module.scss';
import CorporateActionPaymentReportContainer from './components/corporate-action-payment-report-container/corporate-action-payment-report-container';
import IssuerAdhocReportContainer from './components/issuer-adhoc-report-container/issuer-adhoc-report-container';
import EventBasedReportContainer from './components/event-based-report-container/event-based-report-container';
import { IdentityContext } from '../../../../context/identity-context';
import PublishReportModal from './components/modal/publish-report-modal/publish-report-modal';
import RejectReportModal from './components/modal/reject-report-modal/reject-report-modal';
import PdfPreviewModal from './components/modal/pdf-preview-modal/pdf-preview-modal';
import FinalPublishSuccessModal from './components/modal/final-publish-success-modal/final-publish-success-modal';

const corpFields = [
  { key: 'status',      label: 'Status',       type: 'checkbox' },
  { key: 'paymentDate', label: 'Payment Date', type: 'date'     },
  { key: 'issuer',      label: 'Issuer',       type: 'checkbox' }
];

const eventFields = [
  { key: 'reportTitle', label: 'Report Name', type: 'checkbox' },
  { key: 'securityIssuer',     label: 'Issuer',      type: 'checkbox' },
  { key: 'isin',       label: 'ISIN',        type: 'checkbox' },
  { key: 'status',     label: 'Status',      type: 'checkbox' },
  { key: 'dateSubmitted', label: 'Date Submitted', type: 'date'}
];

const issuerFields = [
  { key: 'taxAdjustmentType',    label: 'Report Name',    type: 'checkbox' },
  { key: 'securityIssuer',        label: 'Issuer',         type: 'checkbox' },
  { key: 'bpId',          label: 'BPID',           type: 'checkbox'     },
  { key: 'securityName',  label: 'Security Name',  type: 'checkbox'     },
  { key: 'isin',          label: 'ISIN',           type: 'checkbox' },
  { key: 'issuerRequestDate', label: 'Requested Date', type: 'date'     },
  { key: 'status',        label: 'Status',         type: 'checkbox' }
];

const CheckerReportsModule = () => {
  const reportsService = useMemo(() => new ReportsService(), []);
  const { user } = useContext(IdentityContext);
  const history = useHistory();

  const [activeTab, setActiveTab] = useState('corporate');
  const [reportsData, setReportsData] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState(new Set());
  
  const [corpFilter,   setCorpFilter]   = useState({ status: [], paymentDate: '', issuer: [] });
  const [eventFilter,  setEventFilter]  = useState({ reportTitle: [], issuer: [], isin: [], status: [] });
  const [issuerFilter, setIssuerFilter] = useState({
  taxAdjustmentType: [], issuer: [], bpId: [], securityName: [], isin: [], requestedDate: '', status: []
  });

  const [comments, setComments] = useState('');
  const [publishSuccessMessage, setPublishSuccessMessage] = useState('');
  const [rejectSuccessMessage, setRejectSuccessMessage] = useState('');
  const [isPublishSuccess, setIsPublishSuccess] = useState(false);
  const [isRejectSuccess, setIsRejectSuccess] = useState(false);
  const [finalPublishSuccessMessage, setFinalPublishSuccessMessage] = useState('');
  const [isFinalPublishSuccess, setIsFinalPublishSuccess] = useState(false);
  const [pdfPreviewUrls, setPdfPreviewUrls] = useState([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [previewContinue, setPreviewContinue] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const applyFilters = useCallback((items, filters) => {
    return items.filter(item =>
      Object.entries(filters).every(([key, val]) => {
        if (Array.isArray(val)) {
          return val.length ? val.includes(item[key]) : true;
        }
        return val ? item[key] === val : true;
      })
    );
  }, []);  

  // Fetch the reports data once
  useEffect(() => {
    const getReports = async () => {
      try {
        const result = await reportsService.getReports('checker');
        setReportsData(result);
        // Set default to interest tab data
        setItems(result.interestBasedReports.items);
        setFilteredItems(result.interestBasedReports.items);
      } catch (error) {
        console.error('Fetching error:', error);
        history.push('/internal-server-error');
      } finally {
        setLoading(false);
      }
    };
    getReports();
  }, [history, reportsService]);

  // Update displayed reports when activeTab changes
  useEffect(() => {
    if (reportsData) {
      if (activeTab === 'corporate') {
        setItems(reportsData.interestBasedReports.items);
        setFilteredItems(reportsData.interestBasedReports.items);
      } else if (activeTab === 'issuer') {
        setItems(reportsData.issuerAdhocReports);
        setFilteredItems(reportsData.issuerAdhocReports);
      } else if (activeTab === 'event') {
        // Safely access eventBasedReports and its items property
        setItems(reportsData.eventBasedReports);
        setFilteredItems(reportsData.eventBasedReports);
      }
    }
    setSearchTerm('');
  }, [activeTab, reportsData]);

  // Clear selected reports when the tab changes to avoid combining selections across tabs
  useEffect(() => {
    setSelectedReports(new Set());
  }, [activeTab]);

  const handleFilterChange = useCallback(filters => {
    if (activeTab === 'corporate') {
      setCorpFilter(filters);
      setFilteredItems(applyFilters(items, filters));
    } else if (activeTab === 'event') {
      setEventFilter(filters);
      setFilteredItems(applyFilters(items, filters));
    } else {
      setIssuerFilter(filters);
      setFilteredItems(applyFilters(items, filters));
    }
  }, [activeTab, items, applyFilters]);
  
  const displayedItems = useMemo(() => {
    if (!searchTerm) {
      return filteredItems;
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return filteredItems;
    }

    // Generic search: check any primitive field values
    return filteredItems.filter(item => {
      return Object.entries(item).some(([key, value]) => {
        if (value == null) return false;
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return String(value).toLowerCase().includes(term);
        }
        return false;
      });
    });
  }, [searchTerm, filteredItems, activeTab]);

  // Now using the actual report IDs (not indices)
  const handleCheckboxChange = (id) => {
    setSelectedReports((prev) => {
      const newSelected = new Set(prev);
      newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
      return newSelected;
    });
  };

  const handleReject = () => setShowRejectModal(true);
  const handlePublish = () => setShowPublishModal(true);
  const handleCancel = () => {
    setShowPublishModal(false);
    setShowRejectModal(false);
    setShowPdfPreviewModal(false);
    setComments('');
    setPublishSuccessMessage('');
    setRejectSuccessMessage('');
    setIsPublishSuccess(false);
    setIsRejectSuccess(false);
  };

  const handlePublishContinue = async () => {
    const checkerName = user.attributes['custom:display_name'];
    let allLoaded = true;
    // console.log('Selected reports:', selectedReports);
    try {
      const pdfUrls = [];
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find((item) => item.id === reportId);
        // console.log(report);
        if (report) {
          let fileURL;
          // Check if the report title is "Tax Adjustment Letter"
          if (report.reportTitle === 'Tax Adjustment Letter') {
            // Use the new API with additional options (bpId and taxAdjustmentType)
            const options = {};
            if (report.bpId) options.bpId = report.bpId;
            if (report.taxAdjustmentType) options.taxAdjustmentType = report.taxAdjustmentType;
            const response = await reportsService.retrieveIssuerAdhocReportFile(
              report.reportType,
              report.isin,
              report.reportTitle,
              report.paymentDate,
              options
            );
            fileURL = response.fileURL;
          } 
          else if (['BIR 2306 Request Letter', 'Closure of Registry Service / Payment Account', 'Consent Solicitation Entitlement Report Excel', 'Consent Solicitation Credit Letter', 'Credit Letter'].includes(report.reportTitle))
          {
            var s3Prefix = ``;
            if(report.reportTitle === "BIR 2306 Request Letter")
              s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.bpId}/${report.holder}/${report.dateFrom + report.dateTo}/`;  
            else
              s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.paymentDate}/`;
            const response = await reportsService.retrieveEventBasedReport(s3Prefix)
            fileURL = response.fileURL;
          }
          else {
            const response = await reportsService.retrieveReport(
              report.type,
              report.isin,
              report.title,
              report.paymentDate
            );
            fileURL = response.fileURL;
          }
          pdfUrls.push({ url: fileURL, title: report.title ? report.title : report.reportTitle });
        } else {
          allLoaded = false;
        }
      }
      if (allLoaded) {
        setPdfPreviewUrls(pdfUrls);
        setShowPdfPreviewModal(true);
        setShowPublishModal(false);
      } else {
        setPublishSuccessMessage('Some reports failed to load.');
      }
    } catch (error) {
      console.error('Error while fetching report PDFs:', error);
      setPublishSuccessMessage('Some reports failed to load.');
      setIsPublishSuccess(false);
    }
  };

  const handleFinalPublish = async () => {
    const checkerName = user.attributes['custom:display_name'];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find((item) => item.id === reportId);
        if (report) {
          // If the report title is "Tax Adjustment Letter", use the new API call
          if (report.reportTitle === 'Tax Adjustment Letter') {
            await reportsService.caCheckerSendIssuerAdhocReports(
              report.isin,
              report.bpId,
              report.reportTitle,
              report.taxAdjustmentType,
              report.paymentDate,
              checkerName
            );
          } 
          else if (['BIR 2306 Request Letter', 'Closure of Registry Service / Payment Account', 'Consent Solicitation Entitlement Report Excel', 'Consent Solicitation Credit Letter', 'Credit Letter'].includes(report.reportTitle))
          {
            await reportsService.checkerSendEventBasedReport(
              report.isin, 
              report.reportTitle, 
              report.paymentDate,
              report.bpId,
              report.holder,
              report.dateFrom,
              report.dateTo,
              checkerName
            );
          }
          else {
            await reportsService.checkerSendReport(
              report.type,
              report.title,
              report.isin,
              report.paymentDate,
              checkerName
            );
          }
        }
      }
      setIsFinalPublishSuccess(true);
      setFinalPublishSuccessMessage('Reports successfully published!');
      setShowPdfPreviewModal(false);
    } catch (error) {
      console.error('Error while publishing reports:', error);
      setIsFinalPublishSuccess(false);
      setFinalPublishSuccessMessage('Failed to publish reports.');
      setShowPdfPreviewModal(false);
    }
    setPreviewContinue(false);
  };

  const handleRejectContinue = async () => {
    const checkerName = user.attributes['custom:display_name'];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = items.find((item) => item.id === reportId);
        if (report) {
          if(report.taxAdjustmentType && report.bpId){
            const options = { bpId: report.bpId, taxAdjustmentType: report.taxAdjustmentType };

            await reportsService.checkerRejectReport(
              report.reportType,
              report.reportTitle,
              report.isin,
              report.paymentDate,
              checkerName,
              comments,
              options
            );
          }
          else if (['BIR 2306 Request Letter', 'Closure of Registry Service / Payment Account', 'Consent Solicitation Entitlement Report Excel', 'Consent Solicitation Credit Letter', 'Credit Letter'].includes(report.reportTitle))
          {
            await reportsService.checkerRejectEventBasedReport(
              report.isin, 
              report.reportTitle, 
              report.paymentDate,
              report.bpId,
              report.holder,
              report.dateFrom,
              report.dateTo,
              checkerName,
              comments
            );
          }
          else{
          await reportsService.checkerRejectReport(
            report.type,
            report.title,
            report.isin,
            report.paymentDate,
            checkerName,
            comments
          );}
        }
      }
      setIsRejectSuccess(true);
      setRejectSuccessMessage('Reports successfully rejected!');
    } catch (error) {
      console.error('Error while rejecting reports:', error);
    }
  };

  const areReportsSelected = selectedReports.size > 0;

  return (
    <Container className="checker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* Tabs rendered above both the filter and report container */}
          <Row>
            <Col xs={12}>
              <Tabs
                activeKey={activeTab}
                onSelect={(tab) => setActiveTab(tab)}
                className="mb-3"
              >
                <Tab eventKey="corporate" title="Corporate Action Payment Reports" />
                <Tab eventKey="event" title="Event-Based Reports" />
                <Tab eventKey="issuer" title="Issuer-Adhoc Reports" />
              </Tabs>
            </Col>
          </Row>
          {activeTab === 'corporate' && (
            <Row>
            <Col md={2} className="panel">
              <Filter
                items={items}
                filter={corpFilter}
                onFilterChange={handleFilterChange}
                fields={corpFields}
              />
            </Col>
            <Col md={10}>
                <Container className="reports-container">
                  <Row>
                    <Col xs={12}>
                      <div className="search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search reports..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-box"
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div className="reports-table-container">
                        <CorporateActionPaymentReportContainer
                          filteredReports={displayedItems}
                          handleCheckboxChange={handleCheckboxChange}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div className="actions-container">
                        <Button
                          onClick={handleReject}
                          className="reject-button"
                          disabled={!areReportsSelected}
                          aria-disabled={!areReportsSelected}
                        >
                          Reject Report
                        </Button>
                        <Button
                          onClick={handlePublish}
                          className="publish-button"
                          variant="primary"
                          disabled={!areReportsSelected}
                          aria-disabled={!areReportsSelected}
                        >
                          Publish Report to Issuer
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          )}
          
          { activeTab === 'issuer' && (
            <Row>
          <Col md={2} className="panel">
            <Filter
              items={items}
              filter={issuerFilter}
              onFilterChange={handleFilterChange}
              fields={issuerFields}
            />
          </Col>
          <Col md={10}>
                <Container className="reports-container">
                  <Row className="align-items-center">
                    <Col xs={6}>
                      <h5 className="mx-3">Ad-hoc Reports</h5>
                    </Col>
                    <Col xs={6}>
                      <div className="search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search reports..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-box"
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div>
                        <IssuerAdhocReportContainer
                          filteredReports={displayedItems}
                          handleCheckboxChange={handleCheckboxChange}
                          handleSendReport={handlePublish}
                          handleReject={handleReject}
                        />
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          )}
          { activeTab === 'event' ? (
            <Row>
            <Col md={2} className="panel">
              <Filter
                items={items}
                filter={eventFilter}
                onFilterChange={handleFilterChange}
                fields={eventFields}
              />
            </Col>
            <Col md={10}>
                <Container className="reports-container">
                  <Row>
                    <Col xs={12}>
                      <div className="search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search reports..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-box"
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div className="reports-table-container">
                        <EventBasedReportContainer
                          items={displayedItems}
                          handleCheckboxChange={handleCheckboxChange}
                          handleSendReport={handlePublish}
                          handleReject={handleReject}
                        />
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          ) : null}
          <PublishReportModal
            show={showPublishModal}
            isPublishSuccess={isPublishSuccess}
            publishSuccessMessage={publishSuccessMessage}
            handleCancel={handleCancel}
            handlePublishContinue={handlePublishContinue}
          />
          <RejectReportModal
            show={showRejectModal}
            isRejectSuccess={isRejectSuccess}
            rejectSuccessMessage={rejectSuccessMessage}
            comments={comments}
            setComments={setComments}
            handleCancel={handleCancel}
            handleRejectContinue={handleRejectContinue}
          />
          <PdfPreviewModal
            show={showPdfPreviewModal}
            pdfPreviewUrls={pdfPreviewUrls}
            currentPdfIndex={currentPdfIndex}
            setCurrentPdfIndex={setCurrentPdfIndex}
            handleCancel={handleCancel}
            handleFinalPublish={handleFinalPublish}
          />
          <FinalPublishSuccessModal 
            show={isFinalPublishSuccess}
            onHide={() => setIsFinalPublishSuccess(false)}
            finalPublishSuccessMessage={finalPublishSuccessMessage}
          />
        </>
      )}
    </Container>
  );
};

export default CheckerReportsModule;
